
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function CampsBayPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Camps Bay Painters Cape Town | Luxury Property Painting Services | Premium Coastal Finishes";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Luxury painting services for Camps Bay properties. Premium coastal painting with marine-grade protection. Expert interior & exterior painting for prestigious Camps Bay homes in Cape Town.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Camps Bay painters, luxury property painting, premium coastal painting, Camps Bay homes, Cape Town luxury painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/camps-bay`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Camps Bay Painters - Luxury Property Painting Services",
      "description": "Luxury painting services for Camps Bay properties with premium coastal finishes and marine-grade protection.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/camps-bay`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Camps Bay",
        "description": "Premium luxury painting services for Camps Bay's prestigious coastal properties.",
        "areaServed": "Camps Bay, Cape Town",
        "serviceType": "Luxury Coastal Property Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Camps Bay')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Luxury Villa Painting',
      description: 'Premium painting services for Camps Bay\'s exclusive villas and luxury properties. Specialized techniques for high-end finishes and architectural details with marine-grade protection systems.',
      price: 'From R20,000',
      features: ['Premium paint systems', 'Architectural detailing', 'Custom color matching', 'Luxury interior finishes', 'Marine-grade protection']
    },
    {
      icon: 'ri-building-line',
      title: 'Apartment Painting',
      description: 'High-end interior and exterior painting for Camps Bay apartments and penthouses with stunning ocean and mountain views. Specialized approach for luxury residential complexes.',
      price: 'From R15,000',
      features: ['Penthouse specialization', 'View preservation', 'Luxury materials', 'Coordinated schemes', 'Minimal disruption']
    },
    {
      icon: 'ri-palette-line',
      title: 'Designer Finishes',
      description: 'Specialized decorative finishes and custom color schemes for luxury Camps Bay homes, including textured walls, metallic accents, and artistic applications.',
      price: 'From R10,000',
      features: ['Decorative techniques', 'Texture applications', 'Metallic finishes', 'Artistic murals', 'Custom designs']
    }
  ];

  const luxuryFeatures = [
    {
      title: 'Marine-Grade Protection',
      description: 'Advanced coating systems specifically designed for Camps Bay\'s harsh coastal environment, providing superior protection against salt air, UV radiation, and moisture penetration. Our marine-grade systems extend paint life by up to 300% compared to standard paints.'
    },
    {
      title: 'Architectural Preservation',
      description: 'Specialized techniques for maintaining and enhancing the architectural integrity of Camps Bay\'s prestigious properties while ensuring modern protection standards. We work with heritage consultants when required.'
    },
    {
      title: 'Premium Material Selection',
      description: 'Exclusive use of top-tier paints and finishes from leading international brands including Dulux, Plascon, and imported European systems, ensuring longevity and beauty that matches the luxury standard of Camps Bay properties.'
    },
    {
      title: 'Concierge Service Approach',
      description: 'White-glove service including project management, scheduling coordination, and minimal disruption protocols designed for high-end residential properties. 24/7 project support and emergency response available.'
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Luxury Consultation',
      description: 'Comprehensive property assessment with color consultation, material selection, and custom design planning for your prestigious Camps Bay property. Includes 3D visualization and mood boards.'
    },
    {
      step: 2,
      title: 'Premium Preparation',
      description: 'Meticulous surface preparation using specialized techniques for coastal properties, including salt damage repair, pressure washing, and marine-grade priming systems designed for Camps Bay conditions.'
    },
    {
      step: 3,
      title: 'Expert Application',
      description: 'Professional application by certified craftsmen using premium materials and techniques specifically developed for luxury coastal properties. Multiple quality checkpoints throughout the process.'
    },
    {
      step: 4,
      title: 'Quality Assurance',
      description: 'Comprehensive inspection and quality control process ensuring every detail meets the exacting standards expected in Camps Bay. Includes detailed documentation and warranty coverage.'
    }
  ];

  const environmentalChallenges = [
    {
      title: 'Extreme UV Exposure',
      description: 'Camps Bay receives intense UV radiation due to its western exposure and proximity to the ocean. Our specialized UV-resistant coatings and application techniques ensure long-lasting color retention and surface protection.'
    },
    {
      title: 'Salt Air Corrosion',
      description: 'The constant salt-laden air from the Atlantic Ocean creates unique challenges requiring specialized anti-corrosion treatments and marine-grade protective systems to prevent paint degradation and substrate damage.'
    },
    {
      title: 'Wind and Weather Exposure',
      description: 'Camps Bay\'s exposed position subjects properties to strong south-easter winds and seasonal weather extremes. Our flexible coating systems and reinforced application methods provide superior durability.'
    },
    {
      title: 'Luxury Property Standards',
      description: 'The prestigious nature of Camps Bay properties demands the highest quality finishes and materials. We use only premium, internationally recognized paint systems that meet luxury property standards.'
    }
  ];

  const testimonials = [
    {
      name: 'Alexander Mitchell',
      property: 'Beachfront Penthouse',
      rating: 5,
      text: 'Exceptional service for our Camps Bay penthouse. The attention to detail was outstanding and the results exceeded our expectations. Perfect understanding of luxury standards and the unique requirements of coastal properties. The marine-grade finish has performed flawlessly.',
      image: 'https://public.readdy.ai/ai/img_res/331b1219b681c111a9d3eca172256da9.jpg'
    },
    {
      name: 'Victoria Sterling',
      property: 'Clifton Villa',
      rating: 5,
      text: 'Outstanding work on our villa. The team handled the coastal conditions expertly and the marine-grade finish has been perfect. Highly recommend for luxury properties. The project management was flawless from start to finish and the results speak for themselves.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20homeowner%20at%20Camps%20Bay%20villa%20with%20Twelve%20Apostles%20backdrop%2C%20professionally%20painted%20exterior%2C%20luxury%20coastal%20property%2C%20satisfied%20client%20portrait&width=80&height=80&seq=campsbay2new&orientation=squarish'
    },
    {
      name: 'Richard Blackwood',
      property: 'Mountain View Estate',
      rating: 5,
      text: 'Superb craftsmanship and professionalism. The color consultation was excellent and the final result perfectly complements our property\'s architecture and the Camps Bay setting. Worth every penny for the quality delivered. The team exceeded all expectations.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20man%20homeowner%20at%20Camps%20Bay%20estate%20with%20Twelve%20Apostles%20mountain%20view%2C%20newly%20painted%20exterior%2C%20luxury%20property%2C%20satisfied%20customer&width=80&height=80&seq=campsbay5new&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Clifton Luxury Villa',
      description: 'Complete exterior and interior renovation with premium finishes, custom color palette, and specialized coastal protection systems. This flagship project showcases our expertise in luxury villa restoration and enhancement.',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20villa%20in%20Camps%20Bay%20with%20Twelve%20Apostles%20backdrop%2C%20freshly%20painted%20white%20exterior%2C%20modern%20architecture%2C%20Camps%20Bay%20beach%20views%2C%20professional%20painting%20work&width=400&height=300&seq=campsbay3new&orientation=landscape'
    },
    {
      title: 'Beachfront Apartment Complex',
      description: 'Multi-unit project with coordinated exterior scheme, marine-grade coatings, and luxury interior finishes. Demonstrates our capability in large-scale luxury developments while maintaining individual unit character.',
      image: 'https://readdy.ai/api/search-image?query=Beachfront%20apartment%20complex%20in%20Camps%20Bay%20with%20Twelve%20Apostles%20view%2C%20newly%20painted%20exteriors%2C%20luxury%20coastal%20development%2C%20professional%20painting%20finish&width=400&height=300&seq=campsbay4new&orientation=landscape'
    },
    {
      title: 'Mountain View Mansion',
      description: 'Prestigious property renovation with decorative finishes, custom textures, and comprehensive protection systems. A showcase of our premium residential painting capabilities and attention to architectural detail.',
      image: 'https://readdy.ai/api/search-image?query=Camps%20Bay%20mansion%20with%20Twelve%20Apostles%20mountain%20backdrop%2C%20professional%20exterior%20painting%2C%20luxury%20residential%20property%2C%20modern%20renovation%2C%20quality%20finish%20work&width=400&height=300&seq=campsbay6new&orientation=landscape'
    },
    {
      title: 'Penthouse Interior Design',
      description: 'High-end interior transformation with luxury finishes, color coordination, and premium materials. Exemplifies our interior design and painting expertise for Camps Bay\'s most exclusive properties.',
      image: 'https://readdy.ai/api/search-image?query=Camps%20Bay%20penthouse%20interior%20with%20Twelve%20Apostles%20view%20through%20windows%2C%20professionally%20painted%20walls%2C%20luxury%20decor%2C%20high-end%20residential%20painting&width=400&height=300&seq=campsbay7new&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20luxury%20property%20in%20Camps%20Bay%20with%20Twelve%20Apostles%20mountain%20backdrop%2C%20pristine%20beach%20views%2C%20painting%20equipment%2C%20scaffolding%2C%20upscale%20coastal%20home%2C%20sunny%20day&width=1920&height=1080&seq=campsbay_hero2&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Camps Bay
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Luxury painting services for Camps Bay's prestigious properties. Premium finishes and expert craftsmanship for your exclusive home with over 20 years of experience in luxury coastal properties.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Camps Bay Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Luxury Painting Services for Camps Bay
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Camps Bay stands as Cape Town's most prestigious coastal suburb, renowned for its pristine beaches, luxury properties, and the iconic Twelve Apostles mountain backdrop. This exclusive area demands painting services that match its world-class reputation and unique environmental challenges.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our specialized team has been serving Camps Bay's luxury property market for over two decades, developing advanced techniques and using premium materials specifically designed for this demanding coastal environment. We understand that Camps Bay properties require more than standard painting - they need marine-grade protection systems and luxury finishes that maintain their value and beauty.
              </p>
              <p className="text-lg text-gray-600">
                From beachfront penthouses with panoramic ocean views to mountainside villas with architectural significance, our Camps Bay painting specialists deliver results that exceed the highest expectations while providing long-term protection against the area's unique environmental challenges.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Camps%20Bay%20luxury%20coastal%20properties%20with%20Twelve%20Apostles%20mountain%20backdrop%2C%20pristine%20beach%20setting%2C%20upscale%20residential%20area%2C%20world-class%20coastal%20suburb&width=600&height=400&seq=campsbay8new&orientation=landscape"
                alt="Camps Bay Luxury Properties"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Premium Camps Bay Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Exclusive painting solutions designed for Camps Bay's luxury properties, combining world-class craftsmanship with marine-grade protection systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Luxury Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Luxury Painting Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced techniques and premium materials designed specifically for Camps Bay's prestigious coastal properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {luxuryFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${feature.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Luxury Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive approach developed specifically for Camps Bay's luxury properties, ensuring exceptional results and long-term protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Challenges Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Camps Bay Environmental Challenges
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding and addressing the unique environmental factors that affect luxury coastal properties in Camps Bay.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {environmentalChallenges.map((challenge, index) => (
              <div key={index} className="bg-gray-50 rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{challenge.title}</h3>
                <p className="text-gray-600">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Camps Bay */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Camps Bay?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unmatched expertise in luxury coastal properties combined with premium materials and world-class service standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-vip-crown-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Luxury Specialists</h3>
              <p className="text-gray-600">Over 20 years exclusively serving Camps Bay's most prestigious properties with uncompromising quality and service standards.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Marine-Grade Protection</h3>
              <p className="text-gray-600">Advanced coastal protection systems extending paint life by 300% while maintaining luxury aesthetics and finishes.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-award-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Premium Materials</h3>
              <p className="text-gray-600">Exclusive use of internationally recognized luxury paint systems and materials specifically selected for coastal properties.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-customer-service-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Concierge Service</h3>
              <p className="text-gray-600">White-glove service approach with dedicated project management, 24/7 support, and minimal disruption protocols.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Camps Bay Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent luxury painting projects across Camps Bay's most prestigious properties and developments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Camps Bay Image */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div>
          <img
            src="https://readdy.ai/api/search-image?query=Camps%20Bay%20coastal%20environment%20with%20Twelve%20Apostles%20mountains%2C%20luxury%20properties%2C%20pristine%20beach%2C%20challenging%20coastal%20conditions%20for%20luxury%20property%20maintenance&width=600&height=500&seq=campsbay9new&orientation=portrait"
            alt="Camps Bay Coastal Environment"
            className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
          />
        </div>
      </div>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Luxury Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Testimonials from satisfied Camps Bay property owners who have experienced our luxury painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready for Luxury Painting in Camps Bay?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's premier luxury painting specialists for Camps Bay properties. Experience the difference that premium materials, expert craftsmanship, and dedicated service make for your prestigious coastal home.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* Added email contact button */}
      <div className="text-center my-8">
        <a
          href="mailto:Get a quote"
          className="bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
        >
          Get Quote
        </a>
      </div>

      <Footer />

      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
